import { useState } from 'react';
import { format } from 'date-fns';
import {
  Shield,
  AlertOctagon,
  Users,
  Mail,
  Gauge,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSafetyControls,
  useSafetyStatus,
  useDeactivateSafetyControl,
} from '@/hooks/useEmailBackend';
import type { SafetyControl } from '@/lib/email/backend-api';

export function SafetyControlsManager() {
  const [selectedTab, setSelectedTab] = useState('active');
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);

  const { data: safetyControls } = useSafetyControls();
  const { data: safetyStatus } = useSafetyStatus();
  const deactivate = useDeactivateSafetyControl();

  // Normalize the control type: the DB/API exposes `control_type` but the UI
  // reads `control.type`. Without this, `control.type` is undefined and the
  // Admin tab crashes on `.replace(...)` whenever any control exists.
  const controls = (safetyControls?.data || []).map((c: any) => ({ ...c, type: c.type ?? c.control_type }));
  const activeControls = controls.filter(control => control.is_active);
  const inactiveControls = controls.filter(control => !control.is_active);

  const getControlIcon = (type: SafetyControl['type']) => {
    switch (type) {
      case 'global_pause': return <AlertOctagon className="h-4 w-4" />;
      case 'workspace_pause': return <Users className="h-4 w-4" />;
      case 'account_pause': return <Mail className="h-4 w-4" />;
      case 'rate_limit': return <Gauge className="h-4 w-4" />;
      default: return <Shield className="h-4 w-4" />;
    }
  };

  const getControlColor = (type: SafetyControl['type']) => {
    switch (type) {
      case 'global_pause': return 'destructive';
      case 'workspace_pause': return 'destructive';
      case 'account_pause': return 'default';
      case 'rate_limit': return 'default';
      default: return 'secondary';
    }
  };

  const handleConfirmDeactivate = () => {
    if (!confirmDeactivateId) return;
    const id = confirmDeactivateId;
    deactivate.mutate(id, {
      onSettled: () => setConfirmDeactivateId(null),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Safety Controls</h2>
        <p className="text-muted-foreground">
          Active emergency controls and history. Deactivating a control lifts
          the restriction it created; if the underlying condition re-occurs, the
          system will auto-pause again.
        </p>
      </div>

      {/* System Status Alert */}
      {safetyStatus?.data && !safetyStatus.data.is_healthy && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>System Health Warning:</strong> The email system is currently experiencing
            issues. Active safety controls may be in effect.
          </AlertDescription>
        </Alert>
      )}

      {/* Controls Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active">
            Active Controls ({activeControls.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            History ({inactiveControls.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activeControls.length > 0 ? (
            <div className="space-y-4">
              {activeControls.map((control) => {
                const isDeactivating =
                  deactivate.isPending && deactivate.variables === control.id;
                return (
                  <Card key={control.id} className="border-red-200 bg-red-50">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getControlIcon(control.type)}
                          <div>
                            <CardTitle className="text-base">
                              {control.type.replace('_', ' ').toUpperCase()}
                            </CardTitle>
                            {control.target_id && (
                              <p className="text-sm text-muted-foreground">
                                Target: {control.target_id}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getControlColor(control.type)}>
                            Active
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isDeactivating}
                            onClick={() => setConfirmDeactivateId(control.id)}
                          >
                            {isDeactivating ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                Deactivating...
                              </>
                            ) : (
                              'Deactivate'
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium">Reason:</span>
                          <p className="text-sm text-muted-foreground">{control.reason}</p>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Created: {format(new Date(control.created_at), 'MMM d, yyyy HH:mm')}</span>
                          <span>By: {control.created_by}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <h3 className="text-lg font-medium mb-2">No Active Safety Controls</h3>
                <p className="text-muted-foreground">
                  All email operations are running normally without any active restrictions.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {inactiveControls.length > 0 ? (
            <div className="space-y-3">
              {inactiveControls.map((control) => (
                <Card key={control.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getControlIcon(control.type)}
                        <div>
                          <div className="font-medium">
                            {control.type.replace('_', ' ').toUpperCase()}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {control.reason}
                          </div>
                          {control.target_id && (
                            <div className="text-xs text-muted-foreground">
                              Target: {control.target_id}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline">Inactive</Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          {format(new Date(control.created_at), 'MMM d, HH:mm')}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Clock className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No historical safety controls</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={!!confirmDeactivateId}
        onOpenChange={(open) => {
          if (!open && !deactivate.isPending) setConfirmDeactivateId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this safety control?</AlertDialogTitle>
            <AlertDialogDescription>
              The condition that triggered it may re-occur; in that case, the
              system will auto-pause again. This action is audited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivate.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deactivate.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDeactivate();
              }}
            >
              {deactivate.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deactivating...
                </>
              ) : (
                'Deactivate'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
